import { useContext, useState } from "react";
import {
  PersonFiltersContext,
  PersonFiltersEventContext,
} from "../../../../context/FiltersContext";
import {
  IPersonFirmaFilterSetting,
  QueryFilter,
  PersonFilterKeys,
} from "../../../../interfaces/filters";
import {
  Firmatype,
  PersonDtoFilterInput,
  VirksomhedsformId,
} from "../../../../interfaces/schema";
import { f } from "../../../../util/f";
import { AccordionFilterItem } from "../../../accordion/Accordion";
import AutoCompleteFirma, {
  AutoCompleteFirmaOnSelectedItem,
} from "../../../autocomplete/AutoCompleteFirma";
import AppliedFilterItem from "../../../filter/AppliedFilterItem";
import DateFilter from "../../../filter/date/DateFilter";
import { DateFilterEvent } from "../../../filter/date/types";
import { defaultDateEvent } from "../../../filter/date/dateFilterHelpers";
import { KodeSelect } from "../../../formcontrols/KodeSelect";
import EnumCheckBox from "../../../checkbox/EnumCheckBox";

// helpers
const filterKey: QueryFilter<PersonFilterKeys>["filterKey"] = "personFirma";

type UpdateEvent =
  | {
      type: "firma";
      firmaItem: AutoCompleteFirmaOnSelectedItem;
    }
  | {
      type: "type";
      selectedEnumKeys: Array<keyof typeof Firmatype>;
    }
  | {
      type: "form";
      kodeListeItem: KodeListe;
    }
  | {
      type: "periode";
      dateFilterEvent: DateFilterEvent;
    };

export default function PersonFirmaFilter() {
  // context
  const filters = useContext(PersonFiltersContext);
  const updateFilters = useContext(PersonFiltersEventContext);

  // find active settings
  const settings: IPersonFirmaFilterSetting = filters.find(
    (f) => f.filterKey === filterKey,
  )?.settings;

  // state
  const [latestDateEvent, setLatestDateEvent] =
    useState<DateFilterEvent>(defaultDateEvent);

  // stuff
  const isDateDisabled = !(
    settings?.activeFilters.length ||
    settings?.firmaTyper.length ||
    settings?.virksomhedsFormer.length
  );

  // helper
  function buildFilter(
    updateSettings: IPersonFirmaFilterSetting,
    dateFilterEvent: DateFilterEvent,
  ) {
    return f<PersonDtoFilterInput>()
      .addFilter("personFirma", {
        some: {
          and: [
            updateSettings.activeFilters.length
              ? {
                  or: updateSettings.activeFilters.map((firma) => {
                    return {
                      firmaId: {
                        eq: firma.id,
                      },
                    };
                  }),
                }
              : {},
            updateSettings.virksomhedsFormer.length
              ? {
                  firma: {
                    virksomhedsform: {
                      in: updateSettings.virksomhedsFormer.map((vf) => vf.id),
                    },
                  },
                }
              : {},
            updateSettings.firmaTyper.length
              ? {
                  firma: {
                    firmatype: {
                      in: updateSettings.firmaTyper.map(
                        (key) => Firmatype[key],
                      ),
                    },
                  },
                }
              : {},
            {
              periode: dateFilterEvent.filter,
            },
          ],
        },
      })
      .build();
  }
  // handlers
  function updateFilterHandler(event: UpdateEvent) {
    // firmarelationer
    const firmaRelationerSettting: IPersonFirmaFilterSetting["activeFilters"] =
      settings?.activeFilters || [];
    if (event.type === "firma") {
      if (event.firmaItem) {
        const filterExist = firmaRelationerSettting.some(
          (setting) => setting.id === event.firmaItem?.id,
        );
        if (filterExist) {
          return;
        }

        firmaRelationerSettting.push({
          id: event.firmaItem.id,
          name: event.firmaItem.header,
        });
      }
    }

    // firmatype
    let firmaTypeSettting: IPersonFirmaFilterSetting["firmaTyper"] =
      settings?.firmaTyper || [];
    if (event.type === "type") {
      firmaTypeSettting = event.selectedEnumKeys;
    }

    // virksomhedsform
    const virksomhedsFormSettting: IPersonFirmaFilterSetting["virksomhedsFormer"] =
      settings?.virksomhedsFormer || [];
    if (event.type === "form") {
      if (!event.kodeListeItem.id) {
        return;
      }
      const filterExist = virksomhedsFormSettting.some(
        (setting) => setting.id === event.kodeListeItem.id,
      );
      if (filterExist) {
        return;
      }

      virksomhedsFormSettting.push({
        id: VirksomhedsformId[event.kodeListeItem.id],
        name: event.kodeListeItem.navn,
      });
    }

    // periode
    let dateFilterSetting: IPersonFirmaFilterSetting["dateFilterSettings"] =
      settings?.dateFilterSettings || latestDateEvent;
    let dateFilterEvent = latestDateEvent;
    if (event.type === "periode") {
      if (event.dateFilterEvent) {
        dateFilterEvent = event.dateFilterEvent;
        setLatestDateEvent(event.dateFilterEvent);
      }
      dateFilterSetting = event.dateFilterEvent.settings;
    }

    const updatedSettings: IPersonFirmaFilterSetting = {
      activeFilters: firmaRelationerSettting,
      firmaTyper: firmaTypeSettting,
      virksomhedsFormer: virksomhedsFormSettting,
      dateFilterSettings: dateFilterSetting,
    };

    const canBuildFilter =
      updatedSettings.activeFilters.length ||
      updatedSettings.virksomhedsFormer.length ||
      updatedSettings.firmaTyper.length;

    const filterString = canBuildFilter
      ? buildFilter(updatedSettings, dateFilterEvent)
      : "";

    const queryFilter: QueryFilter<
      PersonFilterKeys,
      IPersonFirmaFilterSetting
    > = {
      filter: filterString,
      filterKey: filterKey,
      settings: updatedSettings,
      parentFilter: "and",
    };

    updateFilters({
      event: "update",
      queryFilter: queryFilter,
    });
  }

  function removeFilterHandler(id: string) {
    const firmaRelationerSettting: IPersonFirmaFilterSetting["activeFilters"] =
      settings?.activeFilters || [];
    const firmaTypeSettting: IPersonFirmaFilterSetting["firmaTyper"] =
      settings?.firmaTyper || [];
    const virksomhedsFormSettting: IPersonFirmaFilterSetting["virksomhedsFormer"] =
      settings?.virksomhedsFormer || [];
    const dateFilterSetting: IPersonFirmaFilterSetting["dateFilterSettings"] =
      settings?.dateFilterSettings || latestDateEvent;

    const updatedSettings: IPersonFirmaFilterSetting = {
      activeFilters: firmaRelationerSettting.filter((firma) => firma.id !== id),
      firmaTyper: firmaTypeSettting,
      virksomhedsFormer: virksomhedsFormSettting.filter(
        (virksomhedsform) => virksomhedsform.id !== id,
      ),
      dateFilterSettings: dateFilterSetting,
    };

    const canBuildFilter =
      updatedSettings.activeFilters.length ||
      updatedSettings.virksomhedsFormer.length ||
      updatedSettings.firmaTyper.length;

    const filterString = canBuildFilter
      ? buildFilter(updatedSettings, latestDateEvent)
      : "";

    const queryFilter: QueryFilter<
      PersonFilterKeys,
      IPersonFirmaFilterSetting
    > = {
      filter: filterString,
      filterKey: filterKey,
      settings: updatedSettings,
      parentFilter: "and",
    };

    if (filterString) {
      updateFilters({
        event: "update",
        queryFilter: queryFilter,
      });
    } else {
      updateFilters({
        event: "delete",
        queryFilter: {
          filterKey: filterKey,
        },
      });
    }
  }

  return (
    <AccordionFilterItem
      title="Firmarelationer"
      value="firma"
      isActive={filters.some((f) => f.filterKey === filterKey)}
    >
      {settings?.activeFilters && (
        <div className="c-applied-filter-group">
          {settings?.activeFilters?.map((filter) => (
            <AppliedFilterItem
              key={filter.id}
              label={filter.name}
              removeFilterHandler={() => removeFilterHandler(filter.id)}
            />
          ))}
        </div>
      )}
      <AutoCompleteFirma
        placeholder="Søg og vælg firma ..."
        includeFirstItem={false}
        sizeSmall
        clearAfterSelection
        onSelectedItemChange={({ selectedItem }) =>
          updateFilterHandler({
            type: "firma",
            firmaItem: selectedItem,
          })
        }
      />
      <h4 className="h6 mb-1 mt-3 text-secondary">Virksomhedstype</h4>
      <div className="l-flex-grid mt-1">
        <EnumCheckBox
          enum={Firmatype}
          state={settings?.firmaTyper}
          onChange={(selectedEnumKeys) =>
            updateFilterHandler({
              type: "type",
              selectedEnumKeys: selectedEnumKeys,
            })
          }
        />
      </div>
      <div>
        <h4 className="h6 mb-1 mt-3 text-secondary">Virksomhedsform</h4>
        {settings?.virksomhedsFormer && (
          <div className="c-applied-filter-group">
            {settings?.virksomhedsFormer?.map((filter) => (
              <AppliedFilterItem
                key={filter.id}
                label={filter.name}
                removeFilterHandler={() => removeFilterHandler(filter.id)}
              />
            ))}
          </div>
        )}
        <KodeSelect
          clearAfterSelection
          kodeListeType="VirksomhedsformKode"
          placeholder="Vælg virksomhedsform ..."
          filterKeys={["navn"]}
          onChange={(item) => {
            updateFilterHandler({
              type: "form",
              kodeListeItem: item,
            });
          }}
        />
      </div>
      <div className="mt-3">
        <DateFilter
          handleChange={(dateFilterEvent) =>
            updateFilterHandler({
              type: "periode",
              dateFilterEvent: dateFilterEvent,
            })
          }
          settings={settings?.dateFilterSettings}
          disabled={isDateDisabled}
          key={JSON.stringify(settings?.dateFilterSettings)}
        />
      </div>
    </AccordionFilterItem>
  );
}
